import { create } from "zustand";
import type { CommentSource } from "./commentModel";

/**
 * "Comment on this part" from the page (backend #128 anchors). A part of the
 * concept or the brief calls `requestComment`; the post's comments open, the
 * composer shows the part as a chip and focuses, and the comment is pinned to
 * it. The part names are the API's: concept title, synopsis, hook, insight,
 * objective, evidence, reference, headline, caption; brief treatment, hook,
 * sound, shot (key = its 1-based number), script, inputs, shoot, slide (key),
 * caption (key = channel), deliverables, rules, legal, art_direction, alt_text.
 *
 * "Show on the page" scrolls to the element marked
 * `data-comment-part="<part>"` (plus `data-comment-key` for a shot, slide or
 * caption) inside `data-comment-source="concept" | "brief"`.
 */
export interface CommentAnchorRequest {
  source: CommentSource;
  part: string;
  key?: string | null;
  /** How the chip reads: "the hook", "shot 03". */
  label: string;
  /** Bumps on each request, so asking for the same part again refocuses. */
  at: number;
}

type Store = {
  request: CommentAnchorRequest | null;
  requestComment: (request: Omit<CommentAnchorRequest, "at">) => void;
  clear: () => void;
};

export const useCommentAnchorStore = create<Store>()((set) => ({
  request: null,
  requestComment: (request) => set({ request: { ...request, at: Date.now() } }),
  clear: () => set({ request: null }),
}));

/** The element a thread's anchor points at, if the page marks it. */
export function anchorElement(source: CommentSource, part: string, key?: string | null): HTMLElement | null {
  const scope = document.querySelector<HTMLElement>(`[data-comment-source="${source}"]`) ?? document;
  const keyed = key ? `[data-comment-key="${CSS.escape(key)}"]` : "";
  return scope.querySelector<HTMLElement>(`[data-comment-part="${CSS.escape(part)}"]${keyed}`);
}
